import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import {
  AlignCenter, AlignJustify, AlignLeft, AlignRight, Bold, Highlighter, ImageIcon, ImagePlus, Italic,
  Link2, List, ListOrdered, Loader2, Minus, Quote, Redo2, RemoveFormatting, Strikethrough, Table2,
  Underline, Undo2, Unlink, Palette, Trash2, Paperclip, MousePointerClick, Youtube as YoutubeIcon,
} from "lucide-react";

const FONT_SIZES = ["12px", "14px", "16px", "18px", "20px", "24px", "30px", "36px", "48px"];

const Btn = ({ onClick, active, title, testId, children, disabled }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    data-testid={testId}
    className={`h-7 min-w-7 px-1.5 inline-flex items-center justify-center rounded text-xs transition-colors disabled:opacity-40 ${active ? "bg-primary text-primary-foreground" : "hover:bg-muted text-foreground"}`}
  >
    {children}
  </button>
);

const Sep = () => <span className="w-px h-5 bg-border mx-0.5" />;

const ColorBtn = ({ title, icon: Icon, onPick, testId, current }) => (
  <label title={title} className="relative h-7 w-7 inline-flex items-center justify-center rounded hover:bg-muted cursor-pointer" data-testid={testId}>
    <Icon className="w-3.5 h-3.5" />
    <span className="absolute bottom-1 left-1.5 right-1.5 h-0.5 rounded" style={{ background: current || "transparent" }} />
    <input type="color" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => onPick(e.target.value)} data-testid={`${testId}-input`} />
  </label>
);

const blockValue = (ed) => (ed.isActive("heading", { level: 1 }) ? "h1" : ed.isActive("heading", { level: 2 }) ? "h2" : ed.isActive("heading", { level: 3 }) ? "h3" : "p");

export const RichTextToolbar = ({ editor, testId }) => {
  const { token } = useAuth();
  const fileRef = useRef(null);
  const attachRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  if (!editor) return null;
  const c = () => editor.chain().focus();
  const t = (n) => `${testId}-${n}`;

  const setBlock = (v) => (v === "p" ? c().setParagraph().run() : c().toggleHeading({ level: Number(v[1]) }).run());
  const setLink = () => {
    const prev = editor.getAttributes("link").href || "https://";
    const url = window.prompt("Link URL (https://…, /page, mailto:…)", prev);
    if (url === null) return;
    if (!url.trim()) return c().unsetLink().run();
    c().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };
  const imageFromUrl = () => {
    const url = window.prompt("Image URL", "https://");
    if (url && url.trim() && url !== "https://") c().setImage({ src: url.trim() }).run();
  };
  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 10 * 1024 * 1024) return toast.error("Image must be under 10MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/cms/media`, fd, { headers: { Authorization: `Bearer ${token}` } });
      c().setImage({ src: res.data.media.url, alt: file.name }).run();
      toast.success("Image added (also saved to Media Library)");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Image upload failed");
    } finally { setUploading(false); }
  };
  const attachFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) return toast.error("File must be under 50MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/cms/media`, fd, { headers: { Authorization: `Bearer ${token}` } });
      const safe = file.name.replace(/[<>&"]/g, "");
      c().insertContent(`<a href="${res.data.media.url}" target="_blank" class="rt-file">Download: ${safe}</a> `).run();
      toast.success("File attached (also saved to Media Library)");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };
  const insertButton = () => {
    const label = window.prompt("Button text", "Learn more");
    if (!label) return;
    const href = window.prompt("Button link (https://… or /page)", "/");
    if (!href || !/^(https:\/\/|\/|mailto:|tel:)/.test(href)) return href && toast.error("Link must start with https://, / , mailto: or tel:");
    c().insertContent(`<a href="${href.replace(/"/g, "")}" class="rt-btn">${label.replace(/[<>&"]/g, "")}</a> `).run();
  };
  const embedVideo = () => {
    const url = window.prompt("YouTube video link", "https://www.youtube.com/watch?v=");
    if (url && /youtube\.com|youtu\.be/.test(url)) c().setYoutubeVideo({ src: url.trim() }).run();
    else if (url) toast.error("Paste a YouTube link (use a Video block for MP4, Vimeo or Google Drive)");
  };
  const inTable = editor.isActive("table");

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-input bg-muted/40 px-1.5 py-1 rounded-t-md" data-testid={t("toolbar")}>
      <Btn title="Undo" onClick={() => c().undo().run()} disabled={!editor.can().undo()} testId={t("undo")}><Undo2 className="w-3.5 h-3.5" /></Btn>
      <Btn title="Redo" onClick={() => c().redo().run()} disabled={!editor.can().redo()} testId={t("redo")}><Redo2 className="w-3.5 h-3.5" /></Btn>
      <Sep />
      <select value={blockValue(editor)} onChange={(e) => setBlock(e.target.value)} className="h-7 rounded border border-input bg-background text-xs px-1" title="Text style" data-testid={t("block")}>
        <option value="p">Paragraph</option><option value="h1">Heading 1</option><option value="h2">Heading 2</option><option value="h3">Heading 3</option>
      </select>
      <select value={editor.getAttributes("textStyle").fontSize || ""} onChange={(e) => (e.target.value ? c().setFontSize(e.target.value).run() : c().unsetFontSize().run())} className="h-7 rounded border border-input bg-background text-xs px-1" title="Font size" data-testid={t("font-size")}>
        <option value="">Size</option>{FONT_SIZES.map((s) => <option key={s} value={s}>{s.replace("px", "")}</option>)}
      </select>
      <Sep />
      <Btn title="Bold" active={editor.isActive("bold")} onClick={() => c().toggleBold().run()} testId={t("bold")}><Bold className="w-3.5 h-3.5" /></Btn>
      <Btn title="Italic" active={editor.isActive("italic")} onClick={() => c().toggleItalic().run()} testId={t("italic")}><Italic className="w-3.5 h-3.5" /></Btn>
      <Btn title="Underline" active={editor.isActive("underline")} onClick={() => c().toggleUnderline().run()} testId={t("underline")}><Underline className="w-3.5 h-3.5" /></Btn>
      <Btn title="Strikethrough" active={editor.isActive("strike")} onClick={() => c().toggleStrike().run()} testId={t("strike")}><Strikethrough className="w-3.5 h-3.5" /></Btn>
      <ColorBtn title="Text colour" icon={Palette} current={editor.getAttributes("textStyle").color} onPick={(v) => c().setColor(v).run()} testId={t("color")} />
      <ColorBtn title="Highlight" icon={Highlighter} current={editor.getAttributes("highlight").color} onPick={(v) => c().setHighlight({ color: v }).run()} testId={t("highlight")} />
      <Sep />
      <Btn title="Align left" active={editor.isActive({ textAlign: "left" })} onClick={() => c().setTextAlign("left").run()} testId={t("align-left")}><AlignLeft className="w-3.5 h-3.5" /></Btn>
      <Btn title="Align centre" active={editor.isActive({ textAlign: "center" })} onClick={() => c().setTextAlign("center").run()} testId={t("align-center")}><AlignCenter className="w-3.5 h-3.5" /></Btn>
      <Btn title="Align right" active={editor.isActive({ textAlign: "right" })} onClick={() => c().setTextAlign("right").run()} testId={t("align-right")}><AlignRight className="w-3.5 h-3.5" /></Btn>
      <Btn title="Justify" active={editor.isActive({ textAlign: "justify" })} onClick={() => c().setTextAlign("justify").run()} testId={t("align-justify")}><AlignJustify className="w-3.5 h-3.5" /></Btn>
      <Sep />
      <Btn title="Bullet list" active={editor.isActive("bulletList")} onClick={() => c().toggleBulletList().run()} testId={t("bullet-list")}><List className="w-3.5 h-3.5" /></Btn>
      <Btn title="Numbered list" active={editor.isActive("orderedList")} onClick={() => c().toggleOrderedList().run()} testId={t("ordered-list")}><ListOrdered className="w-3.5 h-3.5" /></Btn>
      <Btn title="Quote" active={editor.isActive("blockquote")} onClick={() => c().toggleBlockquote().run()} testId={t("quote")}><Quote className="w-3.5 h-3.5" /></Btn>
      <Btn title="Divider line" onClick={() => c().setHorizontalRule().run()} testId={t("hr")}><Minus className="w-3.5 h-3.5" /></Btn>
      <Sep />
      <Btn title="Add / edit link" active={editor.isActive("link")} onClick={setLink} testId={t("link")}><Link2 className="w-3.5 h-3.5" /></Btn>
      {editor.isActive("link") && <Btn title="Remove link" onClick={() => c().unsetLink().run()} testId={t("unlink")}><Unlink className="w-3.5 h-3.5" /></Btn>}
      <Btn title="Upload image" onClick={() => fileRef.current?.click()} disabled={uploading} testId={t("image-upload")}>
        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
      </Btn>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid={t("image-file")} />
      <Btn title="Image from URL" onClick={imageFromUrl} testId={t("image-url")}><ImageIcon className="w-3.5 h-3.5" /></Btn>
      <Btn title="Embed YouTube video" onClick={embedVideo} testId={t("video")}><YoutubeIcon className="w-3.5 h-3.5" /></Btn>
      <Btn title="Attach a file (download link)" onClick={() => attachRef.current?.click()} disabled={uploading} testId={t("attach")}><Paperclip className="w-3.5 h-3.5" /></Btn>
      <input ref={attachRef} type="file" className="hidden" onChange={attachFile} data-testid={t("attach-file")} />
      <Btn title="Insert button" onClick={insertButton} testId={t("button")}><MousePointerClick className="w-3.5 h-3.5" /></Btn>
      <Btn title="Insert table" onClick={() => c().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} testId={t("table")}><Table2 className="w-3.5 h-3.5" /></Btn>
      {inTable && (
        <span className="inline-flex items-center gap-0.5 rounded bg-background border border-input px-0.5" data-testid={t("table-tools")}>
          <Btn title="Add row below" onClick={() => c().addRowAfter().run()} testId={t("row-add")}>+Row</Btn>
          <Btn title="Add column right" onClick={() => c().addColumnAfter().run()} testId={t("col-add")}>+Col</Btn>
          <Btn title="Delete row" onClick={() => c().deleteRow().run()} testId={t("row-del")}>−Row</Btn>
          <Btn title="Delete column" onClick={() => c().deleteColumn().run()} testId={t("col-del")}>−Col</Btn>
          <Btn title="Delete table" onClick={() => c().deleteTable().run()} testId={t("table-del")}><Trash2 className="w-3.5 h-3.5" /></Btn>
        </span>
      )}
      <Sep />
      <Btn title="Clear formatting" onClick={() => c().unsetAllMarks().clearNodes().run()} testId={t("clear")}><RemoveFormatting className="w-3.5 h-3.5" /></Btn>
    </div>
  );
};
